import { type RefObject, useEffect, useRef } from 'react';

import type { ReadableStore } from '@frontend/viewer-core';

import { entityKey as textEntityKey } from '../session/deriveModel';
import type { DrawingModel, OverlayState } from '../session/types';
import { type ToolViewport, bindOverlayFrame } from '../utils/toolUtils';

/** Per-span data carried between the write and measure passes. */
type PendingPlacement = {
  span: HTMLElement;
  place: string;
  /** Non-null only for single-line text, which is width-fitted. */
  fit: { boxWidthPx: number; widthFactor: number } | null;
};

export type TextOverlayDeps = {
  model: ReadableStore<DrawingModel | null>;
  hiddenLayers: ReadableStore<ReadonlySet<number>>;
  overlays: ReadableStore<OverlayState>;
  viewport: ToolViewport;
};

/**
 * Positions the DXF text overlay's spans from the current viewport.
 *
 * Text is not glyph geometry: the backend deliberately ships raw strings plus an
 * oriented box (`DxfText`), and we draw the real string as HTML so it stays
 * selectable and copy-pasteable. This hook is what places it.
 *
 * **Driven by the session stores, not by React.** It subscribes to the viewport
 * and re-places spans on an animation frame, so panning moves the text without a
 * single React render — the same frame budget the WebGL layer runs on. Placement
 * used to hang off a `useLayoutEffect` keyed on a handle that was rebuilt every
 * frame, which worked but tied text to React's commit cadence.
 *
 * Placement rules, in order:
 *
 * - The text's `anchor` — a `[ax, ay]` fraction of its box from bottom-left — is
 *   pinned to that same fraction of the rendered span via `transformOrigin` plus a
 *   matching negative `translate`. This is what keeps left/centre/right-justified
 *   text anchored when the browser's rendered width differs from the CAD box.
 * - `rotation` is applied about that anchor, so rotating never shifts it. The view's
 *   own quarter turns add to it, so text turns with the drawing.
 * - Font size is `charHeight × pxPerUnit`.
 * - `widthFactor` is applied as real horizontal stretch (it is intended, and is
 *   already baked into the box), and then the span is **only ever shrunk** —
 *   uniformly, preserving aspect — if it still overflows its box. Never expanded,
 *   so text stays undistorted and cannot spill into its neighbours.
 * - Multi-line MTEXT gets a fixed box width and reflows instead of being fitted.
 *
 * Work is split into a write pass and a measure pass so that reading `scrollWidth`
 * doesn't force a layout per span — with hundreds of labels, interleaving the
 * writes and reads is the difference between smooth and visibly janky panning.
 */
export const useDxfTextOverlay = ({
  model,
  hiddenLayers,
  overlays,
  viewport,
}: TextOverlayDeps): RefObject<HTMLDivElement> => {
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) {
      return;
    }

    const place = () => {
      const currentModel = model.getState();
      // Same order as the React spans in the text layer (model.texts, not wire).
      const texts = currentModel?.texts ?? [];
      const view = viewport.getState();
      const hidden = hiddenLayers.getState();
      const { pmi: isPmiVisible, layerColoring: isLayerColoringOn } =
        overlays.getState();
      const pmiKeys = currentModel?.pmiEntityKeys;
      const pxPerUnit = view.scale;
      const viewRotationDeg = view.quarterTurns * 90;
      const spans = layer.children;
      const pending: PendingPlacement[] = [];

      // ── Write pass: everything that doesn't depend on a measurement ───────
      for (let index = 0; index < texts.length; index += 1) {
        const span = spans[index] as HTMLElement | undefined;
        if (!span) {
          continue;
        }
        const text = texts[index].text;

        // A dimension's measurement text belongs to the annotation, not to the
        // layer its author drew it on, so it follows the PMI toggle instead —
        // which is exactly why a hidden layer carrying PMI reads as indeterminate.
        const isPmiText = pmiKeys?.has(textEntityKey(text)) ?? false;
        const isTextVisible = isPmiText
          ? isPmiVisible
          : !hidden.has(text.layer);
        if (!isTextVisible) {
          span.style.display = 'none';
          continue;
        }
        span.style.display = 'block';

        // Matches the color of the glyphs this string replaced — but only while
        // layer colouring is on. Switching it off draws the whole drawing in one
        // neutral color, and text is part of the drawing: leaving these spans in
        // their own colours left the labels bright while the geometry underneath
        // went monochrome.
        //
        // Cleared rather than set to a constant, so the neutral falls back to the
        // text layer's own `color` in the shell. That is also exactly what a scene
        // built before `rgb` existed already renders as, so "no color" has one
        // meaning here instead of two.
        span.style.color = isLayerColoringOn && text.rgb ? text.rgb : '';

        const [anchorX, anchorY] = text.anchor ?? [0, 0];

        // The anchor's world position is the box's bottom-left corner (`pos`) plus
        // the anchor's offset within the box, rotated into world space.
        const theta = (text.rotation * Math.PI) / 180;
        const cos = Math.cos(theta);
        const sin = Math.sin(theta);
        const localX = anchorX * text.width;
        const localY = anchorY * text.height;
        const [screenX, screenY] = viewport.worldToScreen([
          text.pos[0] + localX * cos - localY * sin,
          text.pos[1] + localX * sin + localY * cos,
        ]);

        span.style.left = `${screenX}px`;
        span.style.top = `${screenY}px`;
        span.style.fontSize = `${Math.max(1, text.charHeight * pxPerUnit)}px`;

        // CSS y grows downward, so the vertical origin is measured from the top,
        // and a CAD rotation (counter-clockwise) negates into a CSS one.
        const originX = anchorX * 100;
        const originY = (1 - anchorY) * 100;
        span.style.transformOrigin = `${originX}% ${originY}%`;
        const placement = `translate(${-originX}%, ${-originY}%) rotate(${
          viewRotationDeg - text.rotation
        }deg)`;

        if (text.multiline) {
          span.style.whiteSpace = 'pre-wrap';
          span.style.width = `${Math.max(1, text.width * pxPerUnit)}px`;
          pending.push({ span, place: placement, fit: null });
        } else {
          span.style.whiteSpace = 'pre';
          span.style.width = 'auto';
          pending.push({
            span,
            place: placement,
            fit: {
              boxWidthPx: text.width * pxPerUnit,
              widthFactor: text.widthFactor || 1,
            },
          });
        }
      }

      // ── Measure pass: batched reads, then batched writes ─────────────────
      const measured = pending.map(({ span, place: placement, fit }) => ({
        span,
        place: placement,
        fit,
        naturalWidth: fit ? span.scrollWidth || 1 : 0,
      }));

      measured.forEach(({ span, place: placement, fit, naturalWidth }) => {
        if (!fit) {
          span.style.transform = placement;
          return;
        }
        const stretchedWidth = naturalWidth * fit.widthFactor;
        const shrink =
          fit.boxWidthPx > 0 && stretchedWidth > fit.boxWidthPx
            ? fit.boxWidthPx / stretchedWidth
            : 1;
        span.style.transform = `${placement} scale(${
          fit.widthFactor * shrink
        }, ${shrink})`;
      });
    };

    // Spans are committed by the time this effect runs, but the stores may already
    // have moved on, so place once immediately rather than waiting for a change.
    return bindOverlayFrame({
      draw: place,
      subscriptions: [
        viewport.subscribe,
        viewport.size.subscribe,
        hiddenLayers.subscribe,
        model.subscribe,
        overlays.subscribe,
      ],
    });
  }, [model, hiddenLayers, overlays, viewport]);

  return layerRef;
};
